import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../../../constants/config";
import { getStudentProfileUrl, findUserByUsername } from "../../../../utils/urlUtils";
import * as XLSX from "xlsx";

export function useStudents() {
  const navigate = useNavigate();
  const { studentName } = useParams();

  const [students, setStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active");
  const [error, setError] = useState("");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [currentAcademicYear, setCurrentAcademicYear] = useState("");
  const [filteredAcademicYear, setFilteredAcademicYear] = useState("");
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedStudentProfile, setSelectedStudentProfile] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isImporting, setIsImporting] = useState(false);
  const [importSuccess, setImportSuccess] = useState(false);
  const [showProgressDialog, setShowProgressDialog] = useState(false);
  const [importProgress, setImportProgress] = useState({
    totalRecords: 0,
    processedRecords: 0,
    successCount: 0,
    failureCount: 0,
  });
  const importProgressIntervalRef = useRef(null);
  const [sections, setSectionData] = useState([]);
  const [sortColumn, setSortColumn] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [activeTab, setActiveTab] = useState("profile");
  const [studentSubjects, setStudentSubjects] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [classes, setClassData] = useState([]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [studentRouteName, setStudentRouteName] = useState(null);
  const [studentStatusCounts, setStudentStatusCounts] = useState({
    active: 0, inactive: 0, alumni: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(true);
  const [totalStudents, setTotalStudents] = useState(0);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [isImportManualOpen, setIsImportManualOpen] = useState(false);

  useEffect(() => {
    fetchCurrentAcademicYear();
    fetchClasses();
    fetchSections();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedClass, statusFilter, filteredAcademicYear, debouncedSearchQuery, sortColumn, sortDirection, itemsPerPage]);

  useEffect(() => {
    fetchStudents();
  }, [selectedClass, statusFilter, debouncedSearchQuery, sortColumn, sortDirection, itemsPerPage, currentPage, filteredAcademicYear]);

  useEffect(() => {
    fetchStudentStatusCounts();
  }, [filteredAcademicYear, selectedClass]);

  useEffect(() => {
    if (!studentName) return;
    const student = findUserByUsername(students, studentName);
    if (student) {
      setSelectedStudentProfile(student);
      return;
    }
    if (students.length === 0) return;
    let cancelled = false;
    const token = localStorage.getItem("token");
    axiosInstance
      .get(API_ENDPOINTS.usersStudents, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          includeBusRoutes: true,
          academicYear: filteredAcademicYear || undefined,
          search: studentName,
          paginate: true,
          limit: 50,
          status: "all",
        },
      })
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.data?.user || res.data?.data?.students || [];
        const found = findUserByUsername(list, studentName);
        if (found) setSelectedStudentProfile(found);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [studentName, students, filteredAcademicYear]);

  useEffect(() => {
    if (!selectedStudentProfile?._id) { setStudentSubjects([]); return; }
    setLoadingSubjects(true);
    axiosInstance.get(API_ENDPOINTS.studentSubjects(selectedStudentProfile._id))
      .then((res) => setStudentSubjects(res.data?.data?.subjects ?? []))
      .catch(() => setStudentSubjects([]))
      .finally(() => setLoadingSubjects(false));
  }, [selectedStudentProfile?._id]);

  useEffect(() => {
    if (importSuccess) {
      const timer = setTimeout(() => setImportSuccess(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [importSuccess]);


  useEffect(() => {
    const fetchAcademicYears = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axiosInstance.get(API_ENDPOINTS.academicYears, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAcademicYears(response.data.data.academicYears);
      } catch (error) {
        console.error("Failed to fetch academic years:", error);
      }
    };
    fetchAcademicYears();
  }, []);

  useEffect(() => {
    const fetchStudentRoute = async () => {
      if (selectedStudentProfile && selectedStudentProfile.transportationType === "bus") {
        try {
          const token = localStorage.getItem("token");
          const res = await axiosInstance.get(
            API_ENDPOINTS.vehicleRouteStudent(selectedStudentProfile._id),
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (res.data.data.vehicleRoute) {
            setStudentRouteName(
              res.data.data.vehicleRoute?.name ||
              res.data.data.vehicleRoute?.vehicle?.vehicleNumber ||
              "Not assigned"
            );
          } else {
            setStudentRouteName("Not assigned");
          }
        } catch (err) {
          setStudentRouteName("Not assigned");
        }
      } else {
        setStudentRouteName(null);
      }
    };
    fetchStudentRoute();
  }, [selectedStudentProfile]);

  const fetchClasses = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(API_ENDPOINTS.classes, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClassData(response.data.data.classes);
    } catch (error) {
      console.error("Failed to fetch classes:", error);
    }
  };

  const fetchSections = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(API_ENDPOINTS.sections, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.status === "success") {
        setSectionData(response.data.data.sections);
      }
    } catch (error) {
      console.error("Error fetching sections:", error);
      setError("Failed to fetch sections");
    }
  };

  const fetchStudents = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(API_ENDPOINTS.usersStudents, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          includeBusRoutes: true,
          academicYear: filteredAcademicYear || undefined,
          status: statusFilter,
          class: selectedClass === "all" ? undefined : selectedClass,
          search: debouncedSearchQuery || undefined,
          sortBy: sortColumn,
          sortOrder: sortDirection,
          paginate: true,
          page: currentPage,
          limit: itemsPerPage,
        },
      });

      if (response.data.status === "success" && response.data.data) {
        const allStudents = response.data.data.user || response.data.data.students || [];
        setStudents(allStudents);
        setTotalStudents(Number(response.data.data.count || 0));
      } else {
        setError("Unexpected API response structure");
        setStudents([]);
        setTotalStudents(0);
      }
    } catch (error) {
      console.error("API Error:", error.response?.data || error.message);
      setError("Failed to fetch students: " + (error.response?.data?.message || error.message));
      setStudents([]);
      setTotalStudents(0);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStudentStatusCounts = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(API_ENDPOINTS.usersStudents, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          academicYear: filteredAcademicYear || undefined,
          groupByStatus: true,
          status: "all",
          class: selectedClass === "all" ? undefined : selectedClass,
        },
      });
      setStudentStatusCounts(
        response.data?.data?.statusCounts || { active: 0, inactive: 0, alumni: 0 }
      );
    } catch (countError) {
      console.error("Failed to fetch student status counts:", countError);
      setStudentStatusCounts({ active: 0, inactive: 0, alumni: 0 });
    }
  };

  const fetchCurrentAcademicYear = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(API_ENDPOINTS.usersAcademicYear, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const currentYear = response.data.data.academicYear;
      setCurrentAcademicYear(currentYear?._id || "");
      setFilteredAcademicYear(currentYear?._id || "");
    } catch (error) {
      setError("Failed to fetch current academic year");
      setCurrentAcademicYear("");
      setFilteredAcademicYear("");
    }
  };

  const fetchAllStudentsForExport = async () => {
    const token = localStorage.getItem("token");
    const response = await axiosInstance.get(API_ENDPOINTS.usersStudents, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        includeBusRoutes: true,
        academicYear: filteredAcademicYear || undefined,
        status: statusFilter,
        class: selectedClass === "all" ? undefined : selectedClass,
        search: debouncedSearchQuery || undefined,
        sortBy: sortColumn,
        sortOrder: sortDirection,
      },
    });
    return response.data?.data?.user || response.data?.data?.students || [];
  };

  const getFilteredStudents = () => {
    if (!students || !Array.isArray(students)) return [];
    return students;
  };

  const handleUpdateStudent = async (studentId, updates) => {
    try {
      const token = localStorage.getItem("token");
      await axiosInstance.put(API_ENDPOINTS.user(studentId), { ...updates }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchStudents();
      setIsEditModalOpen(false);
      setSelectedStudent(null);
    } catch (error) {
      setError(error.response?.data?.message || "Failed to update student");
    }
  };

  const handleDeleteStudent = async (studentId) => {
    try {
      const token = localStorage.getItem("token");
      await axiosInstance.delete(API_ENDPOINTS.user(studentId), {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchStudents();
    } catch (error) {
      setError(error.response?.data?.message || "Failed to delete student");
    }
  };

  const handleViewProfile = (student) => {
    navigate(getStudentProfileUrl(student), { replace: true });
    setSelectedStudentProfile(student);
  };

  const getFileRowCount = async (file) => {
    return new Promise((resolve, reject) => {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      if (fileExtension === "xlsx" || fileExtension === "xls") {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: "array" });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);
            resolve(jsonData.length > 0 ? jsonData.length : 0);
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      } else if (fileExtension === "csv") {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const text = e.target.result;
            const lines = text.split("\n").filter((line) => line.trim().length > 0);
            resolve(lines.length > 1 ? lines.length - 1 : 0);
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = reject;
        reader.readAsText(file);
      } else {
        reject(new Error("Unsupported file format"));
      }
    });
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsImporting(true);

    let totalRecords = 0;
    try {
      totalRecords = await getFileRowCount(file);
      setImportProgress({ totalRecords, processedRecords: 0, successCount: 0, failureCount: 0 });
      setShowProgressDialog(true);
    } catch (error) {
      console.error("Error parsing file:", error);
    }

    const formData = new FormData();
    formData.append("file", file);

    if (importProgressIntervalRef.current) {
      clearInterval(importProgressIntervalRef.current);
      importProgressIntervalRef.current = null;
    }
    importProgressIntervalRef.current = setInterval(() => {
      setImportProgress((prev) => {
        if (!prev.totalRecords) return prev;
        const cap = Math.floor(prev.totalRecords * 0.92);
        const step = Math.max(1, Math.floor(prev.totalRecords / 120));
        const next = Math.min(prev.processedRecords + step, cap);
        if (next === prev.processedRecords) return prev;
        return { ...prev, processedRecords: next };
      });
    }, 450);

    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.post(API_ENDPOINTS.usersImport, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });

      if (importProgressIntervalRef.current) {
        clearInterval(importProgressIntervalRef.current);
        importProgressIntervalRef.current = null;
      }

      const { imported, updated, skipped, skippedUsers, message } = response.data.data || {};
      const successCount = (imported || 0) + (updated || 0);
      const failureCount = skipped || 0;

      setImportProgress({
        totalRecords: totalRecords || (successCount + failureCount),
        processedRecords: totalRecords || (successCount + failureCount),
        successCount,
        failureCount,
      });

      setTimeout(() => {
        setShowProgressDialog(false);
        if (imported > 0 || updated > 0) {
          setImportSuccess(true);
          const parts = [];
          if (imported > 0) parts.push(`${imported} new student${imported !== 1 ? "s" : ""} imported`);
          if (updated > 0) parts.push(`${updated} student${updated !== 1 ? "s" : ""} updated`);
          setSuccessMessage(parts.join(" and ") + " successfully!");
          setError("");
        } else {
          setImportSuccess(false);
          if (skippedUsers && skippedUsers.length > 0) {
            const errorDetails = skippedUsers
              .map((su) => `Row ${su.row}: ${su.name || "Unknown"} - ${su.reason || "Skipped"}`)
              .join("\n");
            setError(`No students were imported or updated.\n\nSkipped:\n${errorDetails}`);
          } else {
            setError(message || "No students were imported or updated. Please check the file format and data.");
          }
        }
      }, 500);

      fetchStudents();
    } catch (error) {
      if (importProgressIntervalRef.current) {
        clearInterval(importProgressIntervalRef.current);
        importProgressIntervalRef.current = null;
      }
      setShowProgressDialog(false);
      if (error.response) {
        if (error.response.data.invalidRecords) {
          const invalidRecords = error.response.data.invalidRecords;
          setError(
            `Import failed: ${invalidRecords.length} records had errors.\n` +
            invalidRecords.map((record) => `- ${record.name}: ${record.message}`).join("\n")
          );
        } else {
          setError(error.response.data.message || "Failed to import students");
        }
      } else if (error.request) {
        setError("No response received from server. Please check your connection.");
      } else {
        setError("Error setting up request: " + error.message);
      }
    } finally {
      if (importProgressIntervalRef.current) {
        clearInterval(importProgressIntervalRef.current);
        importProgressIntervalRef.current = null;
      }
      setIsImporting(false);
      event.target.value = "";
      setSuccessMessage("");
    }
  };

  return {
    students, isLoading, error, setError,
    totalStudents, selectedClass, setSelectedClass,
    statusFilter, setStatusFilter,
    searchQuery, setSearchQuery, debouncedSearchQuery,
    currentPage, setCurrentPage, itemsPerPage, setItemsPerPage,
    sortColumn, setSortColumn, sortDirection, setSortDirection,
    currentAcademicYear, filteredAcademicYear, setFilteredAcademicYear,
    academicYears, classes, sections, studentStatusCounts,
    selectedStudentProfile, setSelectedStudentProfile,
    selectedStudent, setSelectedStudent,
    studentRouteName,
    studentSubjects, loadingSubjects,
    activeTab, setActiveTab,
    isEditModalOpen, setIsEditModalOpen,
    isModalOpen, setIsModalOpen,
    isDeleteDialogOpen, setIsDeleteDialogOpen,
    isImporting, importProgress, showProgressDialog,
    importSuccess, setImportSuccess,
    successMessage, setSuccessMessage,
    isImportManualOpen, setIsImportManualOpen,
    fetchStudents, handleUpdateStudent, handleDeleteStudent,
    handleViewProfile, handleFileUpload, getFilteredStudents,
    fetchAllStudentsForExport,
  };
}
